import { useQuery } from '@tanstack/react-query'
import { useEffect } from 'react'
import { useActions } from '../../store'
import { useErrorHandler } from '../useErrorHandler'
import { useResponseHandler } from '../useResponseHandler'
import { useAuth, useTenant } from '../'

const { env } = window
const PER_PAGE = 10

const useAdminAffiliations = ({ search, page, sort, all = false }) => {
  const { securedFetch } = useAuth()
  const { handleError } = useErrorHandler()
  const { handleResponse } = useResponseHandler()
  const { progressStart, progressEnd } = useActions()
  const { tenant } = useTenant()

  const getAdminAffiliations = async (tenantId) => {
    let url = `${env.apiUrl}/tenants/${tenantId}/affiliations`
    if (all) {
      url += '?max=100000'
    } else {
      const offset = (page - 1) * PER_PAGE
      const sortParams = sort.reduce((result, item) => {
        result += `&sort=${item.id}:${item.desc ? 'desc' : 'asc'}`
        return result
      }, '')
      const searchParam = search ? `&search=${search}` : ''
      url += `?max=${PER_PAGE}&offset=${offset}${sortParams}${searchParam}`
    }
    const response = await securedFetch(encodeURI(url))
    await handleResponse(response)
    return response.json()
  }

  const query = useQuery({
    queryKey: ['admin-affiliations', tenant?.id, search, page, sort, all],
    queryFn: () => getAdminAffiliations(tenant?.id),
    enabled: !!tenant
  })

  useEffect(() => {
    if (query.isPending) {
      progressStart()
    } else if (query.isSuccess) {
      progressEnd()
    } else if (query.isError) {
      handleError(query.error, 'Error getting groups')
      progressEnd()
    }
  }, [
    query.isPending,
    query.isSuccess,
    query.isError,
    query.error,
    progressStart,
    progressEnd,
    handleError
  ])

  return {
    affiliations: query.data?.items,
    count: query.data?.count,
    total: query.data?.total,
    pageCount: query.data ? Math.ceil(query.data.count / PER_PAGE) : undefined
  }
}

export { useAdminAffiliations }
